import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router-dom";

import CollapseHeader from "../../../core/common/collapse-header";
import { ContactService, Contact } from "./contact.service";
import ContactForm from "./contactForm";
import { showSuccessAlert, showErrorAlert } from "../../../utils/alertUtils";
import { showConfirmationAlert } from "../../uiInterface/base-ui/alertservice";
import { Modal, OverlayTrigger, Tooltip as BootstrapTooltip } from "react-bootstrap";
import { AuthContext } from "../../../context/authcontext";
import TableFilters, {
  FilterState,
  FilterConfig,
} from "../../../components/filters/TableFilters";
import TablePagination from "../../../components/pagination/TablePagination";
import { getAuthHeaders } from "../../../utils/authUtils";
import male from "../../../assets/male.png";
import ExportComponent from "../../../components/ExportComponent";
import CompanyContactFilterDropdown from "../components/CompanyContactFilterDropdown";
import ComposeEmail from "../../application/components/email/ComposeEmail";
import Tooltip from "@mui/material/Tooltip";
import { API_BASE_URL } from "../../../environment";

const formatLocation = (
  city: string,
  state: string,
  country: string
): string => {
  const locationParts = [city, state, country].filter(
    (part) => part && part.trim() !== ""
  );
  return locationParts.length > 0 ? locationParts.join(", ") : "";
};

const spinningStyles = `
  .ti-spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .contact-grid-row {
    margin-right: -10px;
    margin-left: -10px;
  }

  .contact-grid-row > div {
    padding-right: 10px;
    padding-left: 10px;
  }

  @media (max-width: 575px) {
    .contact-grid-row > div {
      width: 100%;
    }
  }
`;

if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = spinningStyles;
  if (!document.head.querySelector("style[data-spinning]")) {
    styleElement.setAttribute("data-spinning", "true");
    document.head.appendChild(styleElement);
  }
}

interface ContactsGridViewProps {
  contacts: any[];
  loading: boolean;
  backgroundLoading?: boolean;
  error: string | null;
  pagination: any;
  fetchContacts: (page?: number) => Promise<void>;
  refreshContacts: () => Promise<void>;
  addContact: (contact: any) => void;
  updateContact: (contact: any) => void;
  removeContact: (contactId: number) => void;
  lastFetched: Date | null;
  isInitialized: boolean;
  currentView: "list" | "grid";
  onViewSwitch: (view: "list" | "grid") => void;
  clearBackgroundLoading?: () => void;
}

const ContactsGridView = ({
  contacts,
  loading,
  backgroundLoading = false,
  error,
  pagination,
  fetchContacts,
  refreshContacts,
  addContact,
  updateContact,
  removeContact,
  currentView,
  onViewSwitch,
  isInitialized,
  clearBackgroundLoading,
  ...rest
}: ContactsGridViewProps) => {
  const navigate = useNavigate();
  const { selectedTenantId } = useContext(AuthContext);

  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formResetTrigger, setFormResetTrigger] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>("");

  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    dateRange: [null, null] as [Date | null, Date | null],
    industryFilter: [],
    countryFilter: [],
    statusFilter: null,
    sortBy: "created_datetime",
    sortOrder: "desc",
  });

  const [additionalFilters, setAdditionalFilters] = useState<Record<string, any>>({});

  const [filteredContactsData, setFilteredContactsData] = useState<any[]>([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  const [pendingContact, setPendingContact] = useState<Partial<Contact> | null>(
    null
  );
    
  const [isLoading, setIsLoading] = useState(false);

  const [isComposeEmailOpen, setIsComposeEmailOpen] = useState(false);
  const [composeOpenCounter, setComposeOpenCounter] = useState(0);
  const [selectedContactEmail, setSelectedContactEmail] = useState("");

  const filterConfig: FilterConfig = useMemo(
    () => ({
      showSearch: true,
      searchPlaceholder: "Search contacts...",
      searchField: "contact_full_name",
      showDateFilter: true,
      dateField: "created_datetime",
      showIndustryFilter: false,
      showCountryFilter: true,
      countryField: "country",
      pageName: "contact" as const,
      defaultSort: {
        field: "created_datetime",
        order: "desc" as const,
      },
    }),
    []
  );

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
    setCurrentPage(1);

    if (
      !newFilters.searchTerm &&
      !newFilters.dateRange[0] &&
      !newFilters.dateRange[1] &&
      newFilters.countryFilter.length === 0
    ) {
      setIsFilterActive(false);
      setFilteredContactsData([]);
    }
  }, []);

  const handleApiFilterChange = useCallback(
    (apiFilters: Record<string, any>) => { },
    []
  );

  const handleFiltersReset = useCallback(() => {
    setIsFilterActive(false);
    setFilteredContactsData([]);
    setCurrentPage(1);
    setFilters({
      searchTerm: "",
      dateRange: [null, null] as [Date | null, Date | null],
      industryFilter: [],
      countryFilter: [],
      statusFilter: null,
      sortBy: "created_datetime",
      sortOrder: "desc",
    });
    setAdditionalFilters({});
    fetchContacts(1);
  }, [fetchContacts]);

  const handleApiCall = useCallback(
    async (url: string) => {
      if (!selectedTenantId) {
        return;
      }

      try {
        setIsFilterLoading(true);

        const headers = getAuthHeaders();

        const response = await fetch(url, {
          method: "GET",
          headers: headers,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.results && Array.isArray(result.results)) {
          setFilteredContactsData(result.results);
          setIsFilterActive(true);
        } else if (result.data && Array.isArray(result.data)) {
          setFilteredContactsData(result.data);
          setIsFilterActive(true);
        } else if (Array.isArray(result)) {
          setFilteredContactsData(result);
          setIsFilterActive(true);
        } else {
          setFilteredContactsData([]);
          setIsFilterActive(true);
        }
      } catch (error: any) {
        console.error("Filter API error:", error);

        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem("authCred");
          localStorage.removeItem("authResponse");
          localStorage.removeItem("authTimestamp");
          localStorage.removeItem("selectedTenantId");
          localStorage.removeItem("tenant_id");

          navigate("/login");
          return;
        }

        setFilteredContactsData([]);
        setIsFilterActive(false);
      } finally {
        setIsFilterLoading(false);
      }
    },
    [selectedTenantId, navigate]
  );

  const handleAdditionalFilterChange = useCallback(
    (dynamicFilters: Record<string, any>) => {
      setAdditionalFilters(dynamicFilters);
      setCurrentPage(1);

      if (!selectedTenantId) return;

      const params = new URLSearchParams();
      params.set("tenant_id", selectedTenantId);
      params.set("page", "1");
      params.set("page_size", "12");

      if (filters.searchTerm) {
        params.set("contact_full_name", filters.searchTerm);
      }

      if (filters.dateRange[0] && filters.dateRange[1]) {
        const startDate = filters.dateRange[0].toISOString().split("T")[0];
        const endDate = filters.dateRange[1].toISOString().split("T")[0];
        const start = new Date(startDate);
        const end = new Date(endDate);
        const dayDifference =
          (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

        if (startDate === endDate || dayDifference <= 1) {
          params.set("created_datetime", endDate);
        } else {
          params.set("created_datetime_gte", startDate);
          params.set("created_datetime_lte", endDate);
        }
      }

      if (filters.countryFilter && filters.countryFilter.length > 0) {
        filters.countryFilter.forEach((country) => {
          params.append("country", country);
        });
      }

      Object.entries(dynamicFilters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          if (key === "teamFilter") {
            params.set("assigned_team_id", value.toString());
          } else if (key === "userFilter") {
            params.set("assigned_user_id", value.toString());
          } else if (key === "sourceFilter") {
            params.set("source", value.toString());
          } else {
            params.set(key, value.toString());
          }
        }
      });

      if (!params.has("is_active")) {
        params.set("is_active", "true");
      }
      const url = `${API_BASE_URL}/contact/?${params.toString()}`;
      handleApiCall(url);
    },
    [selectedTenantId, filters, handleApiCall]
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchContacts(page);
  };

  const createContactUrl = (contact: Contact): string => {
    return `/contacts/${contact.contact_id}`;
  };

  const { filteredContacts, totalFilteredCount } = useMemo(() => {
    if (isFilterActive) {
      return {
        filteredContacts: filteredContactsData,
        totalFilteredCount: filteredContactsData.length,
      };
    }

    if (!Array.isArray(contacts)) {
      return { filteredContacts: [], totalFilteredCount: 0 };
    }

    return {
      filteredContacts: contacts,
      totalFilteredCount: pagination.totalCount,
    };
  }, [contacts, filteredContactsData, isFilterActive, pagination.totalCount]);

  const paginatedContacts = useMemo(() => {
    if (isFilterActive) {
      const startIndex = (currentPage - 1) * 12;
      const endIndex = startIndex + 12;
      return filteredContacts.slice(startIndex, endIndex);
    }
    return filteredContacts;
  }, [filteredContacts, isFilterActive, currentPage]);

  useEffect(() => {
    if (pagination.currentPage && pagination.currentPage !== currentPage) {
      setCurrentPage(pagination.currentPage);
    }
  }, [pagination.currentPage, currentPage]);

  useEffect(() => {
    const addOffcanvas = document.getElementById("add_contact");

    const handleHidden = () => {
      setIsAddContactOpen(false);
    };

    if (addOffcanvas) {
      addOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (addOffcanvas) {
        addOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  const closeOffcanvas = (offcanvasId: string) => {
    const modal = document.getElementById(offcanvasId);
    if (modal) {
      const closeButton = modal.querySelector(
        '[data-bs-dismiss="offcanvas"]'
      ) as HTMLElement;
      if (closeButton) {
        closeButton.click();
      } else {
        const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(
          modal
        );
        if (bsOffcanvas) {
          bsOffcanvas.hide();
        }
      }
    }
  };

  const handleDelete = async (contact: Contact) => {
    const result = await showConfirmationAlert(
      "Delete Contact",
      `Are you sure you want to delete "${contact.first_name} ${contact.last_name}"? This action cannot be undone.`,
      async () => {
        setIsDeleting(true);
        try {
          await ContactService.deleteContact(contact.contact_id);
        } finally {
          setIsDeleting(false);
        }
      }
    );

    if (result.isConfirmed && result.value) {
      removeContact(contact.contact_id);
      setSelectedContact(null);

      showSuccessAlert(
        `Contact "${contact.first_name} ${contact.last_name}" deleted successfully!`
      );
    }
  };

  const handleCreate = async (
    contact: Partial<Contact>,
    skipEmailCheck = false
  ) => {
    setIsSubmitting(true);
    try {
      if (!skipEmailCheck && contact.primary_email?.trim()) {
        const emailExists = await ContactService.checkEmailExists(
          contact.primary_email.trim()
        );

        if (emailExists) {
                closeOffcanvas("add_contact");
                
                const duplicateEmail = contact.primary_email.trim();
                
                const result = await showConfirmationAlert(
                  "Duplicate Contact Found",
                  `A contact with email "${duplicateEmail}" already exists in the same tenant.
Do you still want to create this contact?`,
                  undefined,
                  { confirmButtonText: 'Yes, Create Contact', cancelButtonText: 'No, Cancel' }
                );

                if (result.isConfirmed) {
                  handleCreate(contact, true);
                } else {
                  setSelectedContact(null);
                  setFormResetTrigger((prev) => prev + 1);
                }
                
                setIsSubmitting(false);
                return;
              }
      }

      let newContact: Contact;

      if ((contact as any).avatarFile) {
        const formData = new FormData();

        Object.keys(contact).forEach((key) => {
          if (key === "avatarFile") {
            formData.append("avatar_url", (contact as any)[key]);
          } else if (key !== "avatarFile") {
            const value = (contact as any)[key];
            if (value !== null && value !== undefined) {
              formData.append(key, value.toString());
            }
          }
        });

        newContact = await ContactService.createContactWithAvatar(formData);
      } else {
        newContact = await ContactService.createContact(contact);
      }

      addContact(newContact);

      setSelectedContact(null);
      setFormResetTrigger((prev) => prev + 1);
      closeOffcanvas("add_contact");

      setTimeout(() => {
        showSuccessAlert(
          `Contact "${newContact.first_name} ${newContact.last_name}" created successfully!`
        );
      }, 500);
    } catch (err) {
      console.error("Failed to create contact:", err);
      showErrorAlert("Failed to create contact");
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };



  const handleUpdate = async (id: number, contact: Partial<Contact>) => {
    setIsSubmitting(true);
    try {
      let updatedContact: Contact;

      if ((contact as any).avatarFile) {
        const formData = new FormData();
        formData.append("avatar_url", (contact as any).avatarFile);

        if (contact.tenant_id)
          formData.append("tenant_id", contact.tenant_id.toString());
        if (contact.updated_by)
          formData.append("updated_by", contact.updated_by.toString());

        const originalContact = contacts.find((c) => c.contact_id === id);
        updatedContact = await ContactService.updateContactWithAvatar(
          id,
          formData,
          originalContact
        );
      } else {
        const originalContact = contacts.find((c) => c.contact_id === id);
        updatedContact = await ContactService.updateContact(
          id,
          contact,
          originalContact
        );
      }

      updateContact(updatedContact);
      closeOffcanvas("edit_contact");
      setSelectedContact(null);
      setIsEditing(false);

      setTimeout(() => {
        showSuccessAlert(
          `Contact "${updatedContact.first_name} ${updatedContact.last_name}" updated successfully!`
        );
      }, 500);
    } catch (err) {
      console.error("Failed to update contact:", err);
      showErrorAlert("Failed to update contact");
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (contact: Contact) => {
    flushSync(() => {
      setSelectedContact(contact);
      setIsEditing(true);
    });
  };

  const handlePreview = (contact: Contact) => {
    const contactUrl = createContactUrl(contact);
    navigate(contactUrl);
  };

  const handleEmailClick = (contact: Contact) => {
    if (contact.primary_email) {
      setSelectedContactEmail(contact.primary_email);
      setComposeOpenCounter((prev) => prev + 1);
      setIsComposeEmailOpen(true);
    }
  };

  const handleCloseComposeEmail = () => {
    setIsComposeEmailOpen(false);
    setSelectedContactEmail("");
  };

  const handleMinimizeComposeEmail = () => {
    setIsComposeEmailOpen(false);
  };

  const handleFormSubmit = async (formData: Partial<Contact>) => {
    if (selectedContact) {
      await handleUpdate(selectedContact.contact_id, formData);
    } else {
      await handleCreate(formData);
    }
  };

  const renderContactCard = (contact: Contact) => (
    <div key={contact.contact_id} className="col-xxl-3 col-xl-4 col-md-6">
      <div className="card border shadow">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <Link
                to={createContactUrl(contact)}
                className="avatar avatar-md flex-shrink-0 me-2"
              >
                {contact.avatar_url ? (
                  <img
                    src={contact.avatar_url}
                    alt={`${contact.first_name} ${contact.last_name} avatar`}
                    className="rounded-circle"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector(".fallback-icon")) {
                        const fallbackDiv = document.createElement("div");
                        fallbackDiv.className =
                          "d-flex align-items-center justify-content-center w-100 h-100 bg-light rounded-circle fallback-icon";
                        fallbackDiv.innerHTML =
                          '<i class="ti ti-user" style="font-size: 1.2rem; color: #6c757d;"></i>';
                        parent.appendChild(fallbackDiv);
                      }
                    }}
                  />
                ) : (
                  <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-light rounded-circle">
                    <img
                      src={male}
                      alt="contact avatar"
                      className="rounded-circle"
                    />
                  </div>
                )}
              </Link>
              <div>
                <h6 className="fs-14">
                  <Link to={createContactUrl(contact)} className="fw-medium">
                    {(() => {
                      const fullName = `${contact.salutation ? contact.salutation + ' ' : ''}${contact.first_name || ''} ${contact.last_name || ''}`.trim();
                      return fullName.length > 20 ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={<BootstrapTooltip id={`tooltip-${contact.contact_id}`}>{fullName}</BootstrapTooltip>}
                        >
                          <span>{`${fullName.substring(0, 20)}...`}</span>
                        </OverlayTrigger>
                      ) : (
                        fullName
                      );
                    })()}
                  </Link>
                </h6>
                <p className="text-default mb-0">
                  {contact.job_title || "Position Not Set"}
                </p>
              </div>
            </div>
            <div className="dropdown table-action">
              <Link
                to="#"
                className="action-icon btn btn-icon btn-sm btn-outline-light shadow"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical" />
              </Link>
              <div className="dropdown-menu dropdown-menu-right">
                <Link
                  className="dropdown-item"
                  to="#"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#edit_contact"
                  onClick={() => handleEdit(contact)}
                >
                  <i className="ti ti-edit text-blue" /> Edit
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDelete(contact);
                  }}
                >
                  <i className="ti ti-trash text-danger" /> Delete
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePreview(contact);
                  }}
                >
                  <i className="ti ti-eye text-blue-light" /> Preview
                </Link>
              </div>
            </div>
          </div>
          <div className="d-block">
            <div className="d-flex flex-column">
              <p className="text-default d-inline-flex align-items-center mb-2">
                <i className="ti ti-mail text-dark me-1" />
                {contact.primary_email || "No email"}
              </p>
              <p className="text-default d-inline-flex align-items-center mb-2">
                <i className="ti ti-phone text-dark me-1" />
                {contact.primary_phone || "No phone"}
              </p>
              <p className="text-default d-inline-flex align-items-center mb-2">
                <i className="ti ti-building text-dark me-1" />
                {contact.company_name ||
                  contact.company?.company_name ||
                  contact.company?.name ||
                  (contact.company_id
                    ? `Company ID: ${contact.company_id}`
                    : "No Company")}
              </p>
              <p className="text-default d-inline-flex align-items-center mb-2">
                <i className="ti ti-map-pin text-dark me-1" />
                {formatLocation(contact.city, contact.state, contact.country) ||
                  "Location not set"}
              </p>
            </div>
          </div>
          <div className="d-flex justify-content-center align-items-center mt-3 pt-3 border-top">
            <ul className="social-info">
              {contact.primary_email ? (
                <li>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleEmailClick(contact);
                    }}
                  >
                    <i
                      className="fa-solid fa-envelope"
                      style={{ color: "#824eeb" }}
                    />
                  </Link>
                </li>
              ) : (
                <li>
                  <Tooltip title="Email not set">
                    <i
                      className="fa-solid fa-envelope mx-2 mt-2 mb-2"
                      style={{ color: "#79787aff" }}
                    />
                  </Tooltip>
                </li>
              )}
              {contact.primary_phone ? (
                <li>
                  <Link to={`tel:${contact.primary_phone}`}>
                    <i className="fa fa-phone " style={{ color: "#824eeb" }} />
                  </Link>
                </li>
              ) : (
                <li>
                  <Tooltip title="Phone not set">
                    <i
                      className="fa fa-phone mx-2 mt-2 mb-2"
                      style={{ color: "#79787aff" }}
                    />
                  </Tooltip>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">
                      Contacts
                      <span className="count-title">
                        {filters.searchTerm !== "" ||
                          filters.dateRange[0] !== null ||
                          filters.dateRange[1] !== null ||
                          filters.countryFilter.length > 0 ||
                          isFilterActive
                          ? totalFilteredCount
                          : pagination.totalCount}
                      </span>
                    </h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}

              {/* Alert messages are now handled by SweetAlert2 via alertUtils */}

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-4"></div>
                    <div className="col-sm-8">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        <ExportComponent
                          entityType="contact"
                          filters={{ ...filters, ...additionalFilters }}
                        />
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#add_contact"
                          onClick={() => setIsAddContactOpen(true)}
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add Contact
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <TableFilters
                    config={filterConfig}
                    data={contacts}
                    onFilterChange={handleFilterChange}
                    onApiFilterChange={handleApiFilterChange}
                    onApiCall={handleApiCall}
                    onFiltersReset={handleFiltersReset}
                    tenantId={selectedTenantId || undefined}
                    className="mt-4"
                  />
                  {/* Right Side Icons */}
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    {/* Filter Dropdown */}
                    <CompanyContactFilterDropdown
                      onFilterChange={handleAdditionalFilterChange}
                      currentFilters={additionalFilters}
                      loading={loading}
                      entityType="contact"
                    />
                    {/* View Toggle Icons */}
                    <div className="view-icons">
                      <Link
                        to="#"
                        className={currentView === "list" ? "active" : ""}
                        onClick={(e) => {
                          e.preventDefault();
                          onViewSwitch("list");
                        }}
                      >
                        <i className="ti ti-list-tree" />
                      </Link>
                      <Link
                        to="#"
                        className={currentView === "grid" ? "active" : ""}
                        onClick={(e) => {
                          e.preventDefault();
                          onViewSwitch("grid");
                        }}
                      >
                        <i className="ti ti-grid-dots" />
                      </Link>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="card-body">
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="mt-2">
                        <small className="text-muted">
                          Loading contacts...
                        </small>
                      </div>
                    </div>
                  </div>
                ) : error ? (
                  <div className="card-body">
                    <div className="alert alert-danger text-center">
                      <i className="ti ti-alert-circle me-2"></i>
                      {error}
                    </div>
                  </div>
                ) : (
                  <div className="card-body">
                    <div style={{ position: "relative" }}>
                      {isFilterLoading && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                            minHeight: "200px",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <span>Filtering results...</span>
                          </div>
                        </div>
                      )}
                      {/* Contact Grid */}
                      <div className="row contact-grid-row">
                        {Array.isArray(paginatedContacts)
                          ? paginatedContacts.map((contact) =>
                            renderContactCard(contact)
                          )
                          : []}
                      </div>

                      {/* Pagination */}
                      <TablePagination
                        currentPage={pagination.currentPage || currentPage}
                        pageSize={12}
                        totalItems={
                          isFilterActive
                            ? totalFilteredCount
                            : pagination.totalCount
                        }
                        filteredItems={totalFilteredCount}
                        isFiltered={
                          filters.searchTerm !== "" ||
                          filters.dateRange[0] !== null ||
                          filters.dateRange[1] !== null ||
                          filters.countryFilter.length > 0
                        }
                        onPageChange={handlePageChange}
                        onPageSizeChange={() => { }}
                        entityName="Contacts"
                        loading={loading}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Contact Offcanvas */}
      <div
        className="offcanvas offcanvas-end"
        id="add_contact"
        tabIndex={-1}
        aria-labelledby="add_contactLabel"
        style={{ width: "550px" }}

      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold" id="add_contactLabel">
            <i className="ti ti-user-plus me-2"></i>Add Contact
          </h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {isAddContactOpen && (
            <ContactForm
              onSubmit={handleFormSubmit}
              onCancel={() => { }}
              loading={isSubmitting}
              resetTrigger={formResetTrigger}
              isMainApiCompleted={isInitialized}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </div>

      {/* Edit Contact Offcanvas */}
      <div
        className="offcanvas offcanvas-end"
        id="edit_contact"
        tabIndex={-1}
        aria-labelledby="edit_contactLabel"
        style={{ width: "550px" }}

      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold" id="edit_contactLabel">
            <i className="ti ti-user-edit me-2"></i>Edit Contact
          </h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={() => {
              setSelectedContact(null);
              setIsEditing(false);
            }}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {selectedContact && (
            <ContactForm
              contact={selectedContact ?? undefined}
              onSubmit={handleFormSubmit}
              onCancel={() => {
                setSelectedContact(null);
                setIsEditing(false);
              }}
              loading={isSubmitting}
              isMainApiCompleted={isInitialized}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </div>



      {/* Loading Overlay */}

      {/* Email Duplicate Confirmation Modal */}
      

      {/* Compose Email Modal */}
      <ComposeEmail
        key={`compose-email-${composeOpenCounter}`}
        isOpen={isComposeEmailOpen}
        onClose={handleCloseComposeEmail}
        onMinimize={handleMinimizeComposeEmail}
        draftData={{
          send_to: selectedContactEmail,
          subject: `Re: Contact`,
        }}
        readOnlyRecipient={false}
      />
    </>
  );
};

export default ContactsGridView;
